import { useEffect, useState } from 'react'
import { useAuth } from '@/lib/auth'
import { supabase } from '@/lib/supabase'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { History, MapPin, Calendar } from 'lucide-react'
import { format } from 'date-fns'
import type { SavedSearch } from '@/types/profile'

interface RecentSearchesProps {
  onSearchSelect: (search: SavedSearch) => void;
}

export function RecentSearches({ onSearchSelect }: RecentSearchesProps) {
  const [searches, setSearches] = useState<SavedSearch[]>([])
  const [loading, setLoading] = useState(true)
  const { user } = useAuth()

  useEffect(() => {
    if (user) {
      loadSearches()
    }
  }, [user])

  async function loadSearches() {
    try {
      const { data, error } = await supabase
        .from('saved_searches')
        .select('*')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(5)

      if (error) throw error
      setSearches(data || [])
    } catch (error) {
      console.error('Error loading saved searches:', error)
    } finally {
      setLoading(false)
    }
  }

  function getSearchIcon(type: string) {
    switch (type) {
      case 'flight':
        return <MapPin className="h-4 w-4 text-primary" />
      case 'hotel':
        return <MapPin className="h-4 w-4 text-primary" />
      default:
        return <Calendar className="h-4 w-4 text-primary" />
    }
  }

  function formatSearchDetails(search: SavedSearch) {
    const query = search.query
    switch (search.search_type) {
      case 'flight':
        return `${query.origin} → ${query.destination}`
      case 'hotel':
        return `${query.location}`
      case 'trip':
        return `${query.destination}`
      default:
        return 'Search details not available'
    }
  }

  if (loading) {
    return (
      <Card className="w-[300px]">
        <CardHeader>
          <CardTitle>Recent Searches</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground">
            Loading...
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="w-[300px]">
      <CardHeader>
        <CardTitle>Recent Searches</CardTitle>
      </CardHeader>
      <CardContent>
        {searches.length > 0 ? (
          <div className="space-y-2">
            {searches.map((search) => (
              <button
                key={search.id}
                className="w-full p-3 flex items-start gap-3 rounded-lg hover:bg-muted/50 transition-colors text-left"
                onClick={() => onSearchSelect(search)}
              >
                {getSearchIcon(search.search_type)}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">
                    {formatSearchDetails(search)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(search.created_at), 'MMM d, yyyy h:mm a')}
                  </p>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>No recent searches</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}